import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

import posthog from 'posthog-js';

import LoadingPage from '../common/LoadingPage';

type OfficeContext = {
  isLoading: boolean;
  isReady: boolean;
  isOfficeEnvironment: boolean;
};

const OfficeContext = createContext<OfficeContext | null>(null);

export default function OfficeProvider({ children }: PropsWithChildren) {
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [info, setInfo] = useState<
    { host: Office.HostType; platform: Office.PlatformType } | undefined
  >();

  useEffect(() => {
    Office.initialize = function () {
      // no-op
    };

    Office.onReady((info) => {
      setInfo(info);
      setIsReady(true);

      const diagnostics = Office.context.mailbox?.diagnostics;

      posthog.capture('Office ready', {
        ...info,
        hostName: diagnostics?.hostName,
        OWAView: diagnostics?.OWAView,
        hostVersion: diagnostics?.hostVersion,
      });
    }).catch(console.error);

    setIsLoading(false);
  }, []);

  const value = {
    isLoading,
    isReady,
    isOfficeEnvironment: !!info?.host,
  };

  if (!isReady) return <LoadingPage />;

  return (
    <OfficeContext.Provider value={value}>{children}</OfficeContext.Provider>
  );
}

export const useOffice = (): OfficeContext => {
  const context = useContext(OfficeContext);

  if (!context) {
    throw new Error('useOffice must be used within a OfficeProvider');
  }

  return context;
};
